/**
 * クルーズ船の予約 — 予約 json_data からの読み出しと、スタッフ向けの決まりの文言（2026-11-02）
 *
 * daylist の 🚢（utils/planPrefix.ts cruiseMarkOf）とサイドパネルの「クルーズ船」セクションが使う。判定はここ1か所。
 * LP（new-n-car）が予約作成時に json_data.data へ立てるキー（正: new-n-car frontend/src/lib/reservationJsonData.js）:
 *   クルーズ船         寄港予定で選んだ船 'MSC BELLISSIMA（11/26 入港 08:00／出港 18:00）'（両店舗・2026-10-31〜）
 *   cruise_plan        那覇のクルーズ船プラン morning・midday・afternoon・overnight（2026-11-02〜）
 *   cruise_plan_price  プラン料金（CDW・NOC・港の送迎料・給油・充電込み）
 *   cruise_plan_window プランの時間 '8:00〜16:00'
 *   cruise_pricing     クルーズ船料金表の料金を使った（'1'）・cruise_pricing_days = 使った日数
 *   prepay_required    事前決済が必要（'1'）。港（宮古=平良港・那覇=那覇港）の予約には船を選ばなくても必ず付く
 * どれも無い古い予約は、内訳コメントの「【送迎】… 那覇港 / 平良港」の行で拾う。
 * 料金の決まりの正: booking-platform/md/pricing.md「那覇のクルーズ船プラン」「平良港(クルーズ船)の予約の決まり」「クルーズ船の予約 — 2026-10-31 の決定」
 */
import { extractJsonDataRecord } from './hotelDelivery';

export const CRUISE_KEYS = {
  ship: 'クルーズ船',
  plan: 'cruise_plan',
  planPrice: 'cruise_plan_price',
  planWindow: 'cruise_plan_window',
  pricing: 'cruise_pricing',
  pricingDays: 'cruise_pricing_days',
  prepay: 'prepay_required',
  /** お渡し場所の案内が済んだか（スタッフがサイドパネルで付け外しする。r-cal だけが書く） */
  handoverGuided: 'お渡し場所の案内',
} as const;

export const CRUISE_HANDOVER_GUIDED_VALUE = '済み';

/** プランの日本語名（r-cal lib/cruisePrices.ts CRUISE_PLANS・LP CRUISE_PLAN_LABELS_JA と同じ） */
export const CRUISE_PLAN_LABELS: Record<string, string> = {
  morning: '①朝着', midday: '②昼着', afternoon: '③午後着', overnight: '④1泊',
};

export interface CruiseShip {
  /** 船名 */
  name: string;
  /** 入港日 'M/D' */
  arriveDate: string;
  arrive: string;
  /** 出港日 'M/D'（入港日と同じなら null） */
  departDate: string | null;
  depart: string;
}

export interface CruiseReservation {
  /** 予約に残っている船の文字列（そのまま） */
  shipLabel: string;
  /** 船の文字列を読めたときの中身（読めなければ null。shipLabel をそのまま出す） */
  ship: CruiseShip | null;
  plan: string | null;
  planLabel: string | null;
  /** '8:00〜16:00' */
  planWindow: string;
  planPrice: number | null;
  cruisePricing: boolean;
  cruisePricingDays: number | null;
  prepayRequired: boolean;
  handoverGuided: boolean;
  /** 内訳コメントでしか分からなかった（古い予約） */
  fromCommentOnly: boolean;
}

const str = (v: unknown): string => (v == null ? '' : String(v).trim());
const isOn = (v: unknown): boolean => { const s = str(v).toLowerCase(); return s === '1' || s === 'true'; };
const num = (v: unknown): number | null => { const n = Number(str(v)); return Number.isFinite(n) && n > 0 ? n : null; };

const SHIP_RE = /^(.+?)（(\d{1,2}\/\d{1,2}) 入港 (\d{1,2}:\d{2})／出港 (?:(\d{1,2}\/\d{1,2}) )?(\d{1,2}:\d{2})）$/;
/** LP の callStaffLabel の文字列を読む。読めなければ null */
export function parseCruiseShipLabel(label: string): CruiseShip | null {
  const m = SHIP_RE.exec(str(label));
  if (!m) return null;
  return { name: m[1].trim(), arriveDate: m[2], arrive: m[3], departDate: m[4] || null, depart: m[5] };
}

/** 内訳コメントの送迎の行（LP: 那覇「【送迎】那覇港（クルーズ船）（お渡しは…）」・宮古「【送迎】到着: 平良港（クルーズ船） / …」） */
const COMMENT_PORT_RE = /【送迎】[^\n]*(那覇港|平良港)/;

/**
 * クルーズ船の予約ならその中身、違えば null。
 * @param comment 予約の comment（内訳）。キーの無い古い予約を拾うためだけに使う
 */
export function parseCruiseReservation(jsonData: unknown, comment?: string | null): CruiseReservation | null {
  const data = extractJsonDataRecord(jsonData) || {};
  const shipLabel = str(data[CRUISE_KEYS.ship]);
  const plan = str(data[CRUISE_KEYS.plan]) || null;
  const cruisePricing = isOn(data[CRUISE_KEYS.pricing]);
  const prepayRequired = isOn(data[CRUISE_KEYS.prepay]);
  const byKeys = Boolean(shipLabel || plan || cruisePricing || prepayRequired);
  const byComment = !byKeys && COMMENT_PORT_RE.test(String(comment ?? ''));
  if (!byKeys && !byComment) return null;
  return {
    shipLabel,
    ship: shipLabel ? parseCruiseShipLabel(shipLabel) : null,
    plan,
    planLabel: plan ? (CRUISE_PLAN_LABELS[plan] || plan) : null,
    planWindow: str(data[CRUISE_KEYS.planWindow]),
    planPrice: num(data[CRUISE_KEYS.planPrice]),
    cruisePricing,
    cruisePricingDays: num(data[CRUISE_KEYS.pricingDays]),
    prepayRequired,
    handoverGuided: str(data[CRUISE_KEYS.handoverGuided]) === CRUISE_HANDOVER_GUIDED_VALUE,
    fromCommentOnly: byComment,
  };
}

/** daylist の tooltip / トースト用の1行。例 '🚢 クルーズ船: MSC BELLISSIMA（11/26 入港 08:00／出港 18:00）・①朝着' */
export function formatCruiseSummary(c: CruiseReservation): string {
  const parts = [c.shipLabel || '船の指定なし（日時で予約）'];
  if (c.planLabel) parts.push(`${c.planLabel}${c.planWindow ? ` ${c.planWindow}` : ''}`);
  return `クルーズ船: ${parts.join('・')}`;
}

/** 料金の種類（サイドパネルの「プラン」） */
export function cruisePriceKindLabel(c: CruiseReservation): string {
  if (c.planLabel) return `クルーズ船プラン ${c.planLabel}`;
  if (c.cruisePricing) return `日別料金（クルーズ船料金表${c.cruisePricingDays ? `・${c.cruisePricingDays}日` : ''}）`;
  return '日別料金（通常の料金＋港の送迎料）';
}

export interface CruiseReturnRule {
  /** 返却の期限（例 '16:00（プランの返却）' / '翌日 12:00（1泊）' / '19:00'） */
  deadline: string;
  /** 期限を過ぎたときの料金・扱い（行ごと） */
  overLines: string[];
}

/**
 * 返却の期限と、過ぎたときの料金（正: md/pricing.md）。
 * @param store 'ncar' = 那覇 / 'pit' = 宮古
 * @param start, end 貸出・返却の日時（'YYYY-MM-DD HH:mm' など。1泊かどうかを見るだけ）
 */
export function cruiseReturnRule(c: CruiseReservation, store: 'ncar' | 'pit', start?: string | null, end?: string | null): CruiseReturnRule {
  if (store === 'ncar' && c.plan) {
    const ret = c.planWindow.split('〜')[1] || '';
    return {
      deadline: ret ? `${c.plan === 'overnight' ? '翌日 ' : ''}${ret}（プランの返却）` : 'プランの返却時刻',
      overLines: [
        '超えた分だけ足す（プランの時間の中は時刻料金・深夜早朝手数料なし）',
        '時刻料金: プランの返却より後に始まった時間帯ごとに +¥1,100（19:00 より後の分）',
        '深夜早朝手数料: 実際の時刻の額 − プランの返却時刻の額',
        '日中の超過は +0。プランの時間を外れた 21:00〜翌 5:59 はリクエスト予約',
      ],
    };
  }
  const sd = str(start).slice(0, 10);
  const ed = str(end).slice(0, 10);
  const overnight = Boolean(sd && ed && sd !== ed);
  const lines = [
    store === 'ncar'
      ? '19:00 を過ぎたら「時」ごとに +¥1,100（19:30 = ¥1,100・20:30 = ¥2,200）。20 時以降は那覇の深夜早朝手数料の表も足す'
      : '19:00 を過ぎたら「時」ごとに +¥1,100（19:30 = ¥1,100・20:30 = ¥2,200）',
    '21:00〜翌 5:59 の返却はリクエスト予約',
  ];
  if (overnight) lines.push('翌日 12:00 を過ぎる返却は、スタッフが個別に対応（クルーズ専用の料金なし）');
  return { deadline: overnight ? '翌日 12:00（1泊）' : '19:00', overLines: lines };
}

/**
 * お渡し場所の案内のチェックを付け外しした新しい json_data を返す（元は変更しない）。
 * 形式は {data,types} に正規化し、他のキーはそのまま（lib/hotelDelivery.ts applyHotelDelivery と同じ流儀）。
 */
export function applyCruiseHandoverGuided(
  jsonData: unknown,
  guided: boolean,
): { data: Record<string, unknown>; types: Record<string, unknown> } {
  let jd: unknown = jsonData;
  if (typeof jd === 'string') {
    try { jd = JSON.parse(jd); } catch { jd = null; }
  }
  if (Array.isArray(jd)) jd = jd[0];
  const base = (jd && typeof jd === 'object') ? (jd as Record<string, unknown>) : {};
  const data: Record<string, unknown> = { ...((base.data as Record<string, unknown>) || {}) };
  const types: Record<string, unknown> = { ...((base.types as Record<string, unknown>) || {}) };
  if (guided) {
    data[CRUISE_KEYS.handoverGuided] = CRUISE_HANDOVER_GUIDED_VALUE;
    types[CRUISE_KEYS.handoverGuided] = 'text';
  } else {
    delete data[CRUISE_KEYS.handoverGuided];
    delete types[CRUISE_KEYS.handoverGuided];
  }
  return { ...base, data, types };
}
